import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Building2, Plus, X, Users, UsersRound, Trash2 } from "lucide-react";
import { rank } from "../../lib/rbac";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Branches() {
  const [branches, setBranches] = useState([]);
  const [user, setUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: "", address: "", phone: "" });

  const fetchData = useCallback(async () => {
    try {
      const [b, me] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/branches/overview`),
        axios.get(`${BACKEND_URL}/api/auth/me`),
      ]);
      setBranches(b.data);
      setUser(me.data.user);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const canAdd = user && rank(user.role) >= 80;
  const canDelete = user && rank(user.role) >= 90;

  const addBranch = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/branches`, form);
      setShowModal(false);
      setForm({ name: "", address: "", phone: "" });
      fetchData();
    } catch (e) { alert(e.response?.data?.detail || "Only GM, MD or CEO can add branches."); }
  };

  const deleteBranch = async (b) => {
    if (!window.confirm(`Delete branch "${b.name}"? This cannot be undone.${(b.members || b.staff) ? `\n\nNote: ${b.members} member(s) and ${b.staff} staff are still assigned to this branch and will become unassigned.` : ""}`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/branches/${b.id}`);
      fetchData();
    } catch (e) { alert(e.response?.data?.detail || "Only CEO or MD can delete a branch."); }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-8 duration-700" data-testid="branches-page">
      <div className="flex flex-col md:flex-row justify-between md:items-end gap-6 mb-4">
        <div>
          <h1 className="text-4xl font-serif text-text_primary mb-3">Branches</h1>
          <p className="text-text_secondary text-sm">All gym locations with their member and staff counts.</p>
        </div>
        {canAdd && (
          <button data-testid="add-branch-open-btn" onClick={() => setShowModal(true)} className="bg-primary text-[#111827] px-6 py-3.5 rounded-xl font-bold hover:bg-primary_hover transition-colors flex items-center gap-2 shadow-md">
            <Plus size={20} /> Add Branch
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {branches.length === 0 && <p className="text-text_secondary col-span-full">No branches found.</p>}
        {branches.map((b) => (
          <div key={b.id} data-testid={`branch-card-${b.name}`} className="bg-card_bg border border-border_soft rounded-2xl p-7 shadow-sm hover:border-primary/40 hover:-translate-y-1 transition-all duration-300 relative">
            {canDelete && (
              <button data-testid={`delete-branch-${b.name}`} onClick={() => deleteBranch(b)} title="Delete branch" className="absolute top-5 right-5 text-text_secondary hover:text-accent_danger transition-colors p-1.5 rounded-lg hover:bg-accent_danger/10">
                <Trash2 size={18} />
              </button>
            )}
            <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center text-gold border border-primary/20 mb-5">
              <Building2 size={28} strokeWidth={1.5} />
            </div>
            <h3 className="text-2xl font-bold text-text_primary mb-1 tracking-tight">{b.name}</h3>
            <p className="text-text_secondary text-sm mb-1">{b.address || "—"}</p>
            {b.phone && <p className="text-text_secondary text-xs mb-4">{b.phone}</p>}
            <div className="flex gap-3 mt-5 border-t border-border_soft/50 pt-5">
              <div className="flex items-center gap-2 text-sm text-text_primary"><Users size={16} className="text-gold" /> <span className="font-bold">{b.members}</span> <span className="text-text_secondary text-xs">Members</span></div>
              <div className="flex items-center gap-2 text-sm text-text_primary ml-2"><UsersRound size={16} className="text-gold" /> <span className="font-bold">{b.staff}</span> <span className="text-text_secondary text-xs">Staff</span></div>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div className="bg-card_bg border border-border_soft p-8 rounded-2xl w-full max-w-md relative shadow-2xl">
            <button onClick={() => setShowModal(false)} className="absolute top-6 right-6 text-text_secondary hover:text-text_primary"><X size={20} /></button>
            <h2 className="text-2xl font-serif text-text_primary mb-6">Add Branch</h2>
            <form onSubmit={addBranch} className="space-y-5">
              <input required data-testid="new-branch-name" placeholder="Branch name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary" />
              <input placeholder="Address" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary" />
              <input placeholder="Phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary" />
              <button type="submit" data-testid="save-branch-btn" className="w-full bg-primary text-[#111827] font-bold py-4 rounded-xl hover:bg-primary_hover transition-colors shadow-md">Save Branch</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
